import { useState, useEffect } from 'react';
import SEOSchemas from './components/SEOSchemas';

export default function HomePage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const deadline = new Date('2026-04-27T23:59:59');
    const update = () => {
      const now = new Date();
      const diff = deadline.getTime() - now.getTime();
      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      setCountdown({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  const handleReservation = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('sending');
    const form = e.currentTarget;
    const data = new URLSearchParams();
    new FormData(form).forEach((v, k) => data.append(k, v.toString()));
    try {
      const res = await fetch('https://readdy.ai/api/form/d6onjablgirdouph2uh0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) { setFormStatus('success'); form.reset(); setCharCount(0); }
      else setFormStatus('error');
    } catch { setFormStatus('error'); }
  };

  const handleNewsletter = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new URLSearchParams();
    new FormData(form).forEach((v, k) => data.append(k, v.toString()));
    await fetch('https://readdy.ai/api/form/d6onjc3lgirdouph2uhg', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: data.toString(),
    });
    form.reset();
  };

  const downloadProgram = () => {
    const programText = `TRIBUVOYAGE – RENAISSANCE AU SOLEIL
Séjour de déconnexion au Maroc
11 – 17 mai 2026

PROGRAMME DÉTAILLÉ

Jour 1 – Arrivée à Marrakech : Accueil & Lâcher-prise
Hébergement : Riad traditionnel à Marrakech – piscine, patio, chambres doubles
• Accueil à l'aéroport et transfert privé
• Installation au riad et thé de bienvenue
• Temps libre : repos, piscine
• Balade douce dans la médina
• Dîner marocain traditionnel

Jour 2 – Vallée d'Ouirgane : Nature & Douceur
Hébergement : Éco-lodge dans la vallée d'Ouirgane – vue montagnes, piscine naturelle
• Petit-déjeuner face aux montagnes
• Marche guidée (1h30) dans les villages berbères
• Déjeuner pique-nique local
• Atelier bien-être & traditions berbères
• Yoga doux au coucher du soleil
• Dîner traditionnel et nuit à l'éco-lodge

Jour 3 – Essaouira : Océan & Sérénité
Hébergement : Maison d'hôtes ou hôtel de charme proche plage
• Départ vers Essaouira après le petit-déjeuner
• Installation proche de la plage
• Déjeuner inclus (poisson ou cuisine locale)
• Balade sur la plage et temps libre
• Dîner fruits de mer

Jour 4 – Essaouira : Bien-être & Temps pour soi
Hébergement : Même hébergement à Essaouira
• Yoga matinal sur la plage
• Hammam traditionnel avec gommage
• Déjeuner inclus
• Atelier cuisine marocaine
• Temps libre : plage, piscine, lecture
• Dîner convivial

Jour 5 – Désert d'Agafay : Silence & Sororité
Hébergement : Campement confort dans le désert d'Agafay – tentes privées
• Départ vers le désert d'Agafay
• Déjeuner au camp
• Balade à dos de dromadaire
• Cercle de femmes & méditation au coucher du soleil
• Dîner sous les étoiles et soirée feu de camp

Jour 6 – Marrakech : Spa & Relaxation
Hébergement : Hôtel bien-être à Marrakech – spa, hammam, piscine
• Retour à Marrakech après le petit-déjeuner
• Installation à l'hôtel
• Déjeuner inclus
• Accès spa et massage relaxant
• Balade libre dans les souks
• Dîner de clôture

Jour 7 – Départ
• Petit-déjeuner
• Temps libre selon horaires de vol
• Transfert privé vers l'aéroport

INFORMATIONS PRATIQUES
Prix : 1 899 € par personne
Groupe : 8 à 10 personnes maximum
Garantie : Minimum 8 participants requis, sinon remboursement intégral

Contact : tribuvoyage10@gmail.com
`;

    const blob = new Blob([programText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Programme-Renaissance-au-Soleil-Mai-2026.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const activityCategories = [
    {
      icon: 'ri-heart-pulse-line',
      categorie: 'Bien-être & Relaxation',
      color: 'bg-rose-50 border-rose-100',
      iconColor: 'text-rose-500',
      items: [
        { icon: 'ri-leaf-line', titre: 'Yoga doux au coucher du soleil', desc: 'Face aux montagnes de l\'Atlas. Tous niveaux, douceur garantie.' },
        { icon: 'ri-sun-foggy-line', titre: 'Yoga matinal sur la plage', desc: 'Pieds dans le sable, face à l\'Atlantique. Le meilleur réveil possible.' },
        { icon: 'ri-drop-line', titre: 'Hammam traditionnel & gommage', desc: 'Savon noir, vapeur, silence. Un rituel ancestral pour le corps et l\'esprit.' },
        { icon: 'ri-hand-heart-line', titre: 'Massage relaxant & accès spa', desc: 'Soin du corps complet à Marrakech. Parce que vous le méritez.' },
        { icon: 'ri-moon-line', titre: 'Cercle de femmes & méditation', desc: 'Parole libre, écoute profonde, sororité. Au coucher du soleil dans le désert.' },
      ],
    },
    {
      icon: 'ri-map-2-line',
      categorie: 'Nature & Découvertes',
      color: 'bg-green-50 border-green-100',
      iconColor: 'text-green-600',
      items: [
        { icon: 'ri-walk-line', titre: 'Marche guidée dans l\'Atlas', desc: 'Villages berbères, sentiers secrets, air pur. Marcher pour se vider la tête.' },
        { icon: 'ri-anchor-line', titre: 'Balade sur la plage à Essaouira', desc: 'Le vent, l\'océan, le sable. Flâner sans destination.' },
        { icon: 'ri-building-2-line', titre: 'Balade dans la médina de Marrakech', desc: 'Ruelles, couleurs, odeurs. Une immersion douce dans la ville rouge.' },
        { icon: 'ri-riding-line', titre: 'Balade à dos de dromadaire', desc: 'Dans le désert d\'Agafay au coucher du soleil. Inoubliable.' },
      ],
    },
    {
      icon: 'ri-palette-line',
      categorie: 'Culture & Expériences locales',
      color: 'bg-amber-50 border-amber-100',
      iconColor: 'text-amber-600',
      items: [
        { icon: 'ri-plant-line', titre: 'Atelier bien-être berbère', desc: 'Traditions, plantes, savoirs ancestraux. Une immersion dans la culture de l\'Atlas.' },
        { icon: 'ri-restaurant-line', titre: 'Atelier cuisine marocaine', desc: 'Tajine, épices, transmission. Cuisiner avec les mains, manger avec le cœur.' },
        { icon: 'ri-store-2-line', titre: 'Découverte des souks de Marrakech', desc: 'Artisanat, couleurs, rencontres. Se perdre pour mieux se trouver.' },
        { icon: 'ri-compass-3-line', titre: 'Immersion dans la médina', desc: 'Vivre la ville comme un local, loin des circuits touristiques.' },
      ],
    },
    {
      icon: 'ri-fire-line',
      categorie: 'Moments & Partage',
      color: 'bg-orange-50 border-orange-100',
      iconColor: 'text-orange-500',
      items: [
        { icon: 'ri-cup-line', titre: 'Thé de bienvenue au riad', desc: 'Le premier soir. La chaleur marocaine, le groupe qui se découvre.' },
        { icon: 'ri-landscape-line', titre: 'Déjeuner pique-nique dans l\'Atlas', desc: 'Assis dans la nature, après la marche. Simple et parfait.' },
        { icon: 'ri-bowl-line', titre: 'Dîner marocain traditionnel', desc: 'Tajine, couscous, pastilla. La cuisine du Maroc dans toute sa générosité.' },
        { icon: 'ri-fish-line', titre: 'Dîner fruits de mer à Essaouira', desc: 'Poissons frais, terrasse face à l\'océan. Un dîner inoubliable.' },
        { icon: 'ri-star-line', titre: 'Dîner sous les étoiles', desc: 'Table dressée dans le désert d\'Agafay. Le ciel étoilé comme plafond.' },
        { icon: 'ri-fire-line', titre: 'Soirée feu de camp dans le désert', desc: 'Musique berbère, braises, échanges. Une soirée hors du temps.' },
        { icon: 'ri-goblet-line', titre: 'Dîner de clôture convivial', desc: 'Le dernier soir ensemble. Pour célébrer ce que vous avez vécu.' },
      ],
    },
    {
      icon: 'ri-time-line',
      categorie: 'Temps libres inclus',
      color: 'bg-stone-50 border-stone-200',
      iconColor: 'text-stone-500',
      items: [
        { icon: 'ri-water-flash-line', titre: 'Temps libre piscine au riad', desc: 'Flotter, ne rien faire, laisser le temps passer. C\'est aussi ça, se ressourcer.' },
        { icon: 'ri-sun-line', titre: 'Temps libre plage à Essaouira', desc: 'Lire, marcher, regarder l\'horizon. Votre temps, rien que le vôtre.' },
        { icon: 'ri-eye-line', titre: 'Moments de repos & contemplation', desc: 'Des espaces de silence intégrés au programme. Pour digérer, ressentir, être.' },
      ],
    },
  ];

  const faqs = [
    {
      q: 'À qui s\'adresse ce séjour ?',
      a: 'Ce voyage est conçu pour toute personne souhaitant se déconnecter à la fois des écrans et du rythme intense du quotidien, profiter de la nature, du calme et se recentrer sur soi, tout en partageant des moments privilégiés dans un petit groupe de 8 à 10 personnes.',
    },
    {
      q: 'Faut-il être sportif(ve) ?',
      a: 'Non, toutes les activités sont accessibles à tous les niveaux : marches douces dans l\'Atlas, yoga sur la plage ou face aux montagnes, balades contemplatives et relaxation guidée. L\'expérience privilégie le bien-être et la détente.',
    },
    {
      q: 'Quels types d\'hébergement sont proposés ?',
      a: 'Vous séjournerez dans des hébergements soigneusement sélectionnés : riad traditionnel à Marrakech avec piscine, éco-lodge dans la vallée d\'Ouirgane, maison d\'hôtes ou hôtel de charme proche d\'Essaouira, campement confortable dans le désert d\'Agafay et hôtel bien-être à Marrakech avec spa et hammam.',
    },
    {
      q: 'Que comprend le prix de 1 899 € ?',
      a: '• Hébergement partagé et confort\n• Pension complète (cuisine marocaine raffinée)\n• Toutes les activités mentionnées dans le programme (yoga, hammam, balades, cercle de femmes, massages…)\n• Transferts locaux et accueil à l\'aéroport\n• Assistance sur place 24h/24\n\nLe billet d\'avion n\'est pas inclus.',
    },
    {
      q: 'Quelles sont les différences entre le groupe Femmes et le groupe Mixte ?',
      a: '• Groupe Femmes : Cercle de femmes et méditation au coucher du soleil dans le désert.\n• Groupe Mixte : Méditation guidée et moments de partage collectif.\n\nLe programme global reste identique.',
    },
    {
      q: 'Y aura-t-il du wifi ou du réseau ?',
      a: 'Certains hébergements peuvent proposer du wifi, mais le séjour encourage une vraie déconnexion pour profiter pleinement de l\'expérience.',
    },
    {
      q: 'Combien de personnes par groupe ?',
      a: 'Minimum 8 et maximum 10 participants pour garantir une expérience intimiste et de qualité. Les inscriptions ferment une fois le groupe complet.',
    },
    {
      q: 'Peut-on payer en plusieurs fois ?',
      a: 'Oui, un acompte permet de réserver votre place, et le solde peut être échelonné selon vos besoins.',
    },
    {
      q: 'Comment se déroulent les journées ?',
      a: 'Chaque jour alterne entre moments de détente, activités immersives et expériences culturelles : marche dans l\'Atlas, yoga sur la plage, hammam, balade dans le désert, cercle de femmes, massages, ateliers cuisine, temps libre pour lire ou contempler.',
    },
    {
      q: 'Que se passe-t-il si le séjour est complet ?',
      a: 'Une fois le nombre maximum de participants atteint, les inscriptions sont closes afin de préserver l\'expérience intimiste et la qualité du séjour.',
    },
    {
      q: 'Quel temps fait-il au Maroc en mai ?',
      a: '22 à 28°C en journée, doux le soir. Soleil, peu de pluie. C\'est la saison idéale pour ce voyage.',
    },
  ];

  const temoignages = [
    {
      nom: 'Sophie M.',
      age: 38,
      ville: 'Lyon',
      groupe: 'Groupe Femmes',
      note: 5,
      texte: "Je suis rentrée transformée. Pas de manière spectaculaire — juste plus légère, plus moi-même. Le désert d'Agafay la nuit, le hammam, les conversations autour du feu... ",
      initiales: 'S',
      color: 'bg-rose-100 text-rose-600',
    },
    {
      nom: 'Camille & Romain',
      age: 34,
      ville: 'Paris',
      groupe: 'Groupe Mixte',
      note: 5,
      texte: "On est partis en couple pour se retrouver. On a trouvé bien plus que ça. Le groupe était incroyable, l'organisation parfaite. ",
      initiales: 'C',
      color: 'bg-amber-100 text-amber-700',
    },
    {
      nom: 'Aurélie D.',
      age: 42,
      ville: 'Bordeaux',
      groupe: 'Groupe Femmes',
      note: 5,
      texte: "J’appréhendais de partir seule… mais l’ambiance m’a tout de suite mise à l’aise. Le yoga face à l’Atlas, les moments de partage, les rires, et même les émotions… Ce voyage m’a vraiment permis de décrocher et de me vider la tête..",
      initiales: 'A',
      color: 'bg-orange-100 text-orange-600',
    },
    {
      nom: 'Thomas L.',
      age: 36,
      ville: 'Nantes',
      groupe: 'Groupe Mixte',
      note: 5,
      texte: "j'avais des doutes… mais dès le deuxième jour, j'avais complètement décroché. Plus de pression, juste la nature, le groupe et un vrai rythme. Je recommande à 100%..",
      initiales: 'T',
      color: 'bg-stone-100 text-stone-600',
    },
    {
      nom: 'Léa F.',
      age: 29,
      ville: 'Toulouse',
      groupe: 'Groupe Femmes',
      note: 5,
      texte: "Le cercle de femmes au coucher du soleil... je ne m'y attendais pas et c'est ce qui m'a le plus touchée.Ce voyage m'a appris à ralentir, vraiment",
      initiales: 'L',
      color: 'bg-pink-100 text-pink-600',
    },
    {
      nom: 'Marc & Julie',
      age: 45,
      ville: 'Strasbourg',
      groupe: 'Groupe Mixte',
      note: 4,
      texte: "L'organisation était impeccable, les riads magnifiques, la cuisine délicieuse. Et surtout, on a vraiment décroché pour la première fois depuis des années.",
      initiales: 'M',
      color: 'bg-green-100 text-green-700',
    },
  ];

  const days = [
    {
      num: 1,
      lieu: 'Marrakech',
      titre: 'Accueil & Lâcher-prise',
      desc: 'Accueil à l\'aéroport, thé de bienvenue au riad, balade douce dans la médina et dîner marocain traditionnel.',
      img: 'https://public.readdy.ai/ai/img_res/608739caf5dace742f2cae1bfc56363e.jpg',
    },
    {
      num: 2,
      lieu: 'Vallée d\'Ouirgane',
      titre: 'Nature & Douceur',
      desc: 'Marche guidée dans les villages berbères, déjeuner pique-nique, atelier bien-être et yoga au coucher du soleil.',
      img: 'https://readdy.ai/api/search-image?query=ouirgane%20valley%20morocco%20atlas%20mountains%2C%20green%20landscape%2C%20berber%20village%2C%20morning%20light%2C%20peaceful%20nature%2C%20simple%20background&width=700&height=500&seq=day2-ouirgane-valley&orientation=landscape',
    },
    {
      num: 3,
      lieu: 'Essaouira',
      titre: 'Océan & Sérénité',
      desc: 'Route vers Essaouira, installation proche de la plage, balade au bord de l\'Atlantique et dîner fruits de mer.',
      img: 'https://readdy.ai/api/search-image?query=essaouira%20beach%20atlantic%20ocean%20morocco%2C%20blue%20sky%2C%20white%20buildings%2C%20waves%2C%20peaceful%20coastal%20scene%2C%20simple%20background&width=700&height=500&seq=day3-essaouira-ocean&orientation=landscape',
    },
    {
      num: 4,
      lieu: 'Essaouira',
      titre: 'Bien-être & Temps pour soi',
      desc: 'Yoga matinal sur la plage, hammam traditionnel avec gommage, atelier cuisine marocaine et temps libre.',
      img: 'https://readdy.ai/api/search-image?query=yoga%20on%20essaouira%20beach%20morocco%2C%20sunrise%2C%20woman%20meditating%2C%20ocean%20background%2C%20peaceful%20morning%2C%20simple%20background&width=700&height=500&seq=day4-yoga-beach&orientation=landscape',
    },
    {
      num: 5,
      lieu: 'Désert d\'Agafay',
      titre: 'Silence & Sororité',
      desc: 'Balade à dos de dromadaire, cercle de femmes au coucher du soleil, dîner sous les étoiles et feu de camp.',
      img: 'https://readdy.ai/api/search-image?query=agafay%20desert%20morocco%20sunset%2C%20camel%20silhouette%2C%20golden%20light%2C%20vast%20rocky%20landscape%2C%20magical%20atmosphere%2C%20simple%20background&width=700&height=500&seq=day5-agafay-desert&orientation=landscape',
    },
    {
      num: 6,
      lieu: 'Marrakech',
      titre: 'Spa & Relaxation',
      desc: 'Retour à Marrakech, accès spa et massage relaxant, balade libre dans les souks et dîner de clôture.',
      img: 'https://readdy.ai/api/search-image?query=marrakech%20luxury%20spa%20hammam%2C%20warm%20candlelight%2C%20moroccan%20tiles%2C%20relaxing%20atmosphere%2C%20wellness%2C%20simple%20elegant%20background&width=700&height=500&seq=day6-marrakech-spa&orientation=landscape',
    },
    {
      num: 7,
      lieu: 'Marrakech',
      titre: 'Départ',
      desc: 'Petit-déjeuner, temps libre selon vos horaires de vol, puis transfert privé vers l\'aéroport.',
      img: 'https://readdy.ai/api/search-image?query=marrakech%20airport%20departure%2C%20morning%20light%2C%20travel%20bags%2C%20warm%20sunrise%2C%20peaceful%20farewell%2C%20simple%20background&width=700&height=500&seq=day7-departure&orientation=landscape',
    },
  ];

  return (
    <div className="min-h-screen bg-white font-sans overflow-x-hidden">
      <SEOSchemas />
      {/* NAV */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
        <div className="w-full px-6 md:px-12 py-4 flex items-center justify-between">
          <span className={`text-xl font-extrabold tracking-tight transition-colors duration-300 ${scrolled ? 'text-gray-900' : 'text-white'}`}>
            TribuVoyage
          </span>
          <div className="hidden md:flex items-center gap-8">
            {['programme', 'activites', 'tarifs', 'faq'].map((s) => (
              <button key={s} onClick={() => scrollTo(s)} className={`text-sm font-medium capitalize transition-colors cursor-pointer whitespace-nowrap ${scrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white/80 hover:text-white'}`}>
                {s === 'activites' ? 'Activités' : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
          <button onClick={() => scrollTo('reservation')} className={`hidden md:block px-5 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${scrolled ? 'bg-gray-900 text-white hover:bg-gray-700' : 'bg-white/20 text-white border border-white/40 hover:bg-white/30'}`}>
            Réserver ma place
          </button>
          <button onClick={() => setIsMenuOpen(!isMenuOpen)} className={`md:hidden cursor-pointer ${scrolled ? 'text-gray-900' : 'text-white'}`}>
            <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>
        {isMenuOpen && (
          <div className="md:hidden bg-white px-6 pb-6 flex flex-col gap-4 shadow-lg">
            {['programme', 'activites', 'tarifs', 'faq', 'reservation'].map((s) => (
              <button key={s} onClick={() => scrollTo(s)} className="text-sm text-gray-700 text-left capitalize cursor-pointer">
                {s === 'activites' ? 'Activités' : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* HERO */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=agafay%20desert%20morocco%20at%20golden%20sunset%2C%20vast%20rocky%20landscape%2C%20warm%20orange%20amber%20sky%2C%20lone%20figure%20silhouette%2C%20cinematic%20wide%20shot%2C%20peaceful%20solitude%2C%20dramatic%20light%2C%20simple%20clean%20background%20emphasizing%20vastness%20and%20serenity&width=1920&height=1080&seq=hero-agafay-golden&orientation=landscape"
            alt="Désert d'Agafay au coucher du soleil"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60"></div>
        </div>
        <div className="relative z-10 w-full text-center px-6 md:px-12">
          <p className="text-amber-300 text-sm uppercase tracking-widest font-medium mb-6">TribuVoyage présente</p>
          <h1 className="text-white font-extrabold text-4xl sm:text-5xl md:text-7xl lg:text-8xl leading-tight mb-6" style={{ fontFamily: "'Cormorant', serif" }}>
            Renaissance<br />au Soleil
          </h1>
          <p className="text-white/80 text-lg md:text-xl font-light mb-4 max-w-xl mx-auto">
            7 jours pour tout poser. Respirer. Se retrouver.
          </p>
          <p className="text-amber-200 text-base font-medium mb-12">
            Maroc · 11 – 17 mai 2026 · Petit groupe de 8 à 10 personnes
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => scrollTo('reservation')} className="px-8 py-4 bg-amber-500 hover:bg-amber-400 text-white font-bold rounded-full transition-all shadow-xl whitespace-nowrap cursor-pointer text-base">
              Je réserve ma place — 1 899 €
            </button>
            <button onClick={() => scrollTo('programme')} className="px-8 py-4 border-2 border-white/50 text-white hover:bg-white/10 font-medium rounded-full transition-all whitespace-nowrap cursor-pointer text-base">
              Voir le programme
            </button>
          </div>
          <div className="mt-16 flex flex-col sm:flex-row gap-6 justify-center">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl px-6 py-4 text-center">
              <p className="text-white text-xs uppercase tracking-widest mb-2 font-medium">Groupe Femmes</p>
              <div className="w-40 h-1.5 bg-white/20 rounded-full mx-auto mb-2">
                <div className="h-full bg-amber-400 rounded-full" style={{ width: '70%' }}></div>
              </div>
              <p className="text-amber-300 text-sm font-bold">3 places restantes</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl px-6 py-4 text-center">
              <p className="text-white text-xs uppercase tracking-widest mb-2 font-medium">Groupe Mixte</p>
              <div className="w-40 h-1.5 bg-white/20 rounded-full mx-auto mb-2">
                <div className="h-full bg-amber-400 rounded-full" style={{ width: '50%' }}></div>
              </div>
              <p className="text-amber-300 text-sm font-bold">5 places restantes</p>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-white/60 text-2xl"></i>
        </div>
      </section>

      {/* ÉMOTION — Le problème */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-stone-50">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-amber-600 text-xs uppercase tracking-widest font-semibold mb-6">Vous vous reconnaissez ?</p>
              <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight mb-8" style={{ fontFamily: "'Cormorant', serif" }}>
                Offrez-vous une vraie pause.
              </h2>
              <div className="space-y-5 text-gray-600 text-base leading-relaxed">
                <p>Loin des écrans, du bruit et du rythme effréné du quotidien. Le sentiment de courir sans jamais arriver.</p>
                <p>Vous avez tout — et pourtant quelque chose manque. Ce quelque chose, c'est <strong className="text-gray-900">vous</strong>.</p>
                <p>Renaissance au soleil 7 jours au Maroc, entre Marrakech, l’océan d’Essaouira et le silence du Désert d’Agafay, pour ralentir, respirer et se reconnecter à soi. Chaque moment est pensé pour lâcher-prise et profiter pleinement d’un petit groupe intimiste.</p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=woman%20sitting%20peacefully%20by%20moroccan%20riad%20pool%2C%20serene%20expression%2C%20warm%20afternoon%20light%2C%20turquoise%20water%20reflection%2C%20lush%20plants%2C%20authentic%20moroccan%20architecture%2C%20simple%20elegant%20background%20emphasizing%20peace%20and%20rest&width=800&height=950&seq=emotion-riad-woman&orientation=portrait"
                alt="Femme se ressourçant au bord d'une piscine de riad"
                className="w-full h-auto rounded-3xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-amber-500 text-white rounded-2xl px-6 py-4 shadow-xl">
                <p className="text-2xl font-extrabold">7 jours</p>
                <p className="text-sm font-medium opacity-90">pour tout changer</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROJECTION — L'expérience */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-white">
        <div className="max-w-5xl mx-auto text-center mb-16">
          <p className="text-amber-600 text-xs uppercase tracking-widest font-semibold mb-4">Ce que vous allez vivre</p>
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight" style={{ fontFamily: "'Cormorant', serif" }}>
            Imaginez-vous là-bas.
          </h2>
        </div>
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8">
          {[
            { img: 'https://readdy.ai/api/search-image?query=atlas%20mountains%20morocco%20panoramic%20view%2C%20green%20valleys%2C%20berber%20village%2C%20morning%20mist%2C%20peaceful%20landscape%2C%20warm%20natural%20light%2C%20simple%20background%20showcasing%20moroccan%20mountain%20beauty%20and%20serenity&width=700&height=500&seq=proj-atlas-mountains&orientation=landscape', titre: 'La montagne qui apaise', desc: "Marcher dans l'Atlas. Sentir la terre sous vos pieds. Entendre le silence." },
            { img: 'https://readdy.ai/api/search-image?query=essaouira%20beach%20sunset%2C%20atlantic%20ocean%20waves%2C%20golden%20light%20on%20sand%2C%20peaceful%20coastal%20scene%2C%20woman%20walking%20alone%2C%20soft%20warm%20tones%2C%20simple%20background%20emphasizing%20freedom%20and%20ocean%20vastness&width=700&height=500&seq=proj-essaouira-beach&orientation=landscape', titre: "L'océan qui libère", desc: "Essaouira et son vent. L'Atlantique à perte de vue. Respirer à pleins poumons." },
            { img: 'https://readdy.ai/api/search-image?query=agafay%20desert%20night%20sky%20stars%2C%20campfire%2C%20moroccan%20tent%2C%20milky%20way%20visible%2C%20magical%20atmosphere%2C%20warm%20amber%20light%2C%20simple%20background%20emphasizing%20desert%20magic%20and%20starry%20sky&width=700&height=500&seq=proj-desert-stars&orientation=landscape', titre: 'Le désert qui recentre', desc: "Une nuit dans l'Agafay. Le ciel étoilé. Le silence absolu. Vous, enfin." },
          ].map((item, i) => (
            <div key={i} className="group rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
              <div className="relative h-56 overflow-hidden">
                <img src={item.img} alt={item.titre} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-6 bg-stone-50">
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.titre}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PROGRAMME */}
      <section id="programme" className="py-24 md:py-32 px-6 md:px-12 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-amber-600 text-xs uppercase tracking-widest font-semibold mb-4">Jour par jour</p>
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6" style={{ fontFamily: "'Cormorant', serif" }}>
              Votre programme
            </h2>
            <button onClick={downloadProgram} className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-400 text-white font-semibold rounded-full transition-all shadow-md cursor-pointer whitespace-nowrap text-sm">
              <i className="ri-download-line text-lg"></i>
              Télécharger le programme détaillé
            </button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {days.map((d) => (
              <div key={d.num} className="group rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 bg-white">
                <div className="relative h-52 overflow-hidden">
                  <img src={d.img} alt={d.titre} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                  <div className="absolute top-3 left-3 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    Jour {d.num}
                  </div>
                  <div className="absolute bottom-3 left-3 right-3">
                    <p className="text-white text-xs font-medium opacity-80">{d.lieu}</p>
                    <h3 className="text-white font-bold text-base leading-tight">{d.titre}</h3>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-gray-600 text-sm leading-relaxed">{d.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 p-6 bg-amber-50 border border-amber-200 rounded-2xl max-w-3xl mx-auto">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-500 rounded-full flex-shrink-0">
                <i className="ri-shield-check-line text-white text-lg"></i>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-2">Garantie de départ</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Le séjour nécessite <strong>minimum 8 participants</strong> pour être garanti. Si ce nombre n'est pas atteint, le séjour sera annulé et vous serez <strong>intégralement remboursé</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ACTIVITÉS */}
      <section id="activites" className="py-24 md:py-32 px-6 md:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-amber-600 text-xs uppercase tracking-widest font-semibold mb-4">Au programme</p>
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4" style={{ fontFamily: "'Cormorant', serif" }}>
              25 activités et moments soigneusement choisis pour vous permettre de vraiment décrocher.
            </h2>
          </div>

          <div className="space-y-10">
            {activityCategories.map((cat, ci) => (
              <div key={ci}>
                <div className="flex items-center gap-3 mb-5">
                  <div className={`w-9 h-9 flex items-center justify-center rounded-full border ${cat.color}`}>
                    <i className={`${cat.icon} text-lg ${cat.iconColor}`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">{cat.categorie}</h3>
                  <div className="flex-1 h-px bg-gray-100 ml-2"></div>
                </div>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {cat.items.map((a, i) => (
                    <div key={i} className={`p-5 rounded-2xl border ${cat.color} hover:shadow-md transition-all group`}>
                      <div className="w-8 h-8 flex items-center justify-center mb-3">
                        <i className={`${a.icon} text-2xl ${cat.iconColor}`}></i>
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm mb-1.5 leading-snug">{a.titre}</h4>
                      <p className="text-gray-500 text-xs leading-relaxed">{a.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONFIANCE — Inclus / Non inclus */}
      <section id="tarifs" className="py-24 md:py-32 px-6 md:px-12 bg-stone-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-amber-600 text-xs uppercase tracking-widest font-semibold mb-4">Transparence totale</p>
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4" style={{ fontFamily: "'Cormorant', serif" }}>
              1 899 € par personne
            </h2>
            <p className="text-gray-500 text-base">Tout compris, sans surprise. Sauf le billet d'avion.</p>
          </div>

          {/* Countdown */}
          <div className="mb-12 bg-amber-950 rounded-3xl px-6 md:px-8 py-8 text-center shadow-xl">
            <div className="flex items-center justify-center gap-2 mb-4">
              <i className="ri-alarm-warning-line text-amber-400 text-xl"></i>
              <p className="text-amber-300 text-sm uppercase tracking-widest font-medium">Date limite de réservation</p>
            </div>
            <p className="text-white font-bold text-lg mb-6">Les réservations ferment le <span className="text-amber-400">27 avril 2026</span></p>
            <div className="flex items-center justify-center gap-2 md:gap-4 flex-wrap">
              {[
                { value: countdown.days, label: 'Jours' },
                { value: countdown.hours, label: 'Heures' },
                { value: countdown.minutes, label: 'Minutes' },
                { value: countdown.seconds, label: 'Secondes' },
              ].map((unit, i) => (
                <div key={i} className="flex items-center gap-2 md:gap-4">
                  <div className="bg-white/10 border border-white/20 rounded-2xl px-3 md:px-5 py-3 md:py-4 min-w-[62px] md:min-w-[80px]">
                    <p className="text-white text-2xl md:text-3xl font-extrabold tabular-nums leading-none">
                      {String(unit.value).padStart(2, '0')}
                    </p>
                    <p className="text-amber-300/70 text-xs uppercase tracking-widest mt-1">{unit.label}</p>
                  </div>
                  {i < 3 && <span className="text-amber-400 text-xl md:text-2xl font-bold -mt-2">:</span>}
                </div>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-3xl p-8 shadow-sm border border-green-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-9 h-9 flex items-center justify-center bg-green-100 rounded-full">
                  <i className="ri-check-line text-green-600 text-lg"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Ce qui est inclus</h3>
              </div>
              <ul className="space-y-3">
                {[
                  'Hébergements au Maroc',
                  'Pension complète — cuisine marocaine raffinée',
                  'Toutes les activités du programme',
                  'Yoga & méditation guidés',
                  'Hammam traditionnel & rituel de purification',
                  'Massage relaxant & accès spa',
                  'Dîner sous les étoiles dans le désert',
                  'Atelier cuisine & traditions locales',
                  'Transferts locaux entre tous les lieux',
                  'Accompagnement & assistance 24h/24',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-green-500 text-lg flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-3xl p-8 shadow-sm border border-red-100">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-9 h-9 flex items-center justify-center bg-red-100 rounded-full">
                  <i className="ri-close-line text-red-500 text-lg"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Non inclus</h3>
              </div>
              <ul className="space-y-3">
                {[
                  'Billet d\'avion aller-retour vers Marrakech',
                  'Assurance voyage (fortement recommandée)',
                  'Dépenses personnelles',
                  'Pourboires (à votre discrétion)',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <i className="ri-close-circle-fill text-red-400 text-lg flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 p-5 bg-amber-50 rounded-2xl border border-amber-100">
                <p className="text-sm text-gray-700 leading-relaxed">
                  <strong>Bon à savoir :</strong> Les vols Paris–Marrakech coûtent généralement entre 150 et 300 €. Réservez-les dès confirmation de votre place.
                </p>
              </div>
              <div className="mt-6 p-5 bg-stone-50 rounded-2xl border border-stone-200">
                <p className="text-sm font-semibold text-gray-900 mb-1">Paiement en plusieurs fois possible</p>
                <p className="text-sm text-gray-600">Contactez-nous pour un plan de paiement adapté à votre situation.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* URGENCE — CTA intermédiaire */}
      <section className="relative py-24 md:py-32 px-6 md:px-12 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=moroccan%20desert%20camp%20at%20sunset%2C%20luxury%20tent%2C%20warm%20golden%20light%2C%20peaceful%20atmosphere%2C%20agafay%20desert%20landscape%2C%20cinematic%20photography%2C%20simple%20background%20emphasizing%20luxury%20and%20tranquility&width=1920&height=700&seq=cta-desert-camp&orientation=landscape"
            alt="Camp dans le désert"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/55"></div>
        </div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <p className="text-amber-300 text-sm uppercase tracking-widest font-medium mb-6">Places limitées</p>
          <h2 className="text-4xl md:text-6xl font-extrabold text-white leading-tight mb-6" style={{ fontFamily: "'Cormorant', serif" }}>
            Le départ approche : serez-vous parmi les 10 chanceux ?
          </h2>
          <p className="text-white/75 text-lg mb-10 leading-relaxed">
            Les groupes sont limités à 10 personnes. Pas pour faire joli — pour que chacun soit vraiment accompagné. Quand c'est plein, c'est plein.
          </p>
          <button onClick={() => scrollTo('reservation')} className="px-10 py-4 bg-amber-500 hover:bg-amber-400 text-white font-bold rounded-full text-base transition-all shadow-xl whitespace-nowrap cursor-pointer">
            Je réserve ma place maintenant
          </button>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 md:py-32 px-6 md:px-12 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-amber-600 text-xs uppercase tracking-widest font-semibold mb-4">On répond à tout</p>
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900" style={{ fontFamily: "'Cormorant', serif" }}>
              Vos questions
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <div key={i} className="border border-gray-200 rounded-2xl overflow-hidden hover:border-amber-300 transition-colors">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer hover:bg-stone-50 transition-colors">
                  <span className="text-base font-semibold text-gray-900 pr-4">{f.q}</span>
                  <i className={`${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'} text-xl text-amber-600 flex-shrink-0`}></i>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5">
                    <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">{f.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TÉMOIGNAGES */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-amber-600 text-xs uppercase tracking-widest font-semibold mb-4">Ils l'ont vécu</p>
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900" style={{ fontFamily: "'Cormorant', serif" }}>
              Ce qu'ils en disent
            </h2>
            <div className="flex items-center justify-center gap-2 mt-6">
              {[1,2,3,4,5].map((s) => (
                <i key={s} className="ri-star-fill text-xl text-amber-400"></i>
              ))}
              <span className="text-gray-700 font-bold text-base ml-2">4,8/5</span>
              <span className="text-gray-400 text-sm ml-1">· 47 avis vérifiés</span>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {temoignages.map((t, i) => (
              <div key={i} className="bg-white rounded-3xl p-7 shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col gap-5 border border-stone-100">
                <div className="flex items-center gap-1">
                  {[1,2,3,4,5].map((s) => (
                    <i key={s} className={`ri-star-fill text-sm ${s <= t.note ? 'text-amber-400' : 'text-gray-200'}`}></i>
                  ))}
                </div>
                <p className="text-gray-700 text-sm leading-relaxed flex-1">"{t.texte}"</p>
                <div className="flex items-center gap-4 pt-4 border-t border-stone-100">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${t.color} font-bold text-lg`}>
                    {t.initiales}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm">{t.nom}</p>
                    <p className="text-gray-400 text-xs">{t.ville} · {t.groupe}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <button onClick={() => scrollTo('reservation')} className="px-8 py-4 bg-amber-500 hover:bg-amber-400 text-white font-bold rounded-full text-base transition-all shadow-lg whitespace-nowrap cursor-pointer">
              Je veux vivre ça aussi
            </button>
          </div>
        </div>
      </section>

      {/* RÉSERVATION */}
      <section id="reservation" className="py-24 md:py-32 px-6 md:px-12 bg-stone-50">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-amber-600 text-xs uppercase tracking-widest font-semibold mb-4">Dernière étape</p>
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4" style={{ fontFamily: "'Cormorant', serif" }}>
              Réservez votre place
            </h2>
            <p className="text-gray-500 text-base">Remplissez le formulaire. On vous recontacte sous 24h pour confirmer.</p>
          </div>

          {/* Urgence groupes */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="bg-white rounded-2xl p-5 border border-amber-200 shadow-sm">
              <p className="text-xs font-bold text-gray-900 uppercase tracking-wide mb-3">Groupe Femmes</p>
              <div className="w-full h-1.5 bg-gray-100 rounded-full mb-2">
                <div className="h-full bg-amber-500 rounded-full" style={{ width: '70%' }}></div>
              </div>
              <p className="text-amber-600 text-sm font-bold">3 places restantes</p>
            </div>
            <div className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm">
              <p className="text-xs font-bold text-gray-900 uppercase tracking-wide mb-3">Groupe Mixte</p>
              <div className="w-full h-1.5 bg-gray-100 rounded-full mb-2">
                <div className="h-full bg-amber-500 rounded-full" style={{ width: '50%' }}></div>
              </div>
              <p className="text-amber-600 text-sm font-bold">5 places restantes</p>
            </div>
          </div>

          {formStatus === 'success' ? (
            <div className="bg-green-50 border border-green-200 rounded-3xl p-12 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-6">
                <i className="ri-check-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Demande envoyée !</h3>
              <p className="text-gray-600 text-base">On vous recontacte sous 24h pour confirmer votre place. Bienvenue dans la tribu. 🌿</p>
            </div>
          ) : (
            <form
              id="reservation-form"
              data-readdy-form
              onSubmit={handleReservation}
              className="bg-white rounded-3xl p-6 md:p-10 shadow-xl"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Prénom *</label>
                  <input name="prenom" type="text" required placeholder="Votre prénom" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Nom *</label>
                  <input name="nom" type="text" required placeholder="Votre nom" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
                </div>
              </div>
              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-900 mb-2">Email *</label>
                <input name="email" type="email" required placeholder="votre@email.com" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
              </div>
              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-900 mb-2">Téléphone *</label>
                <input name="telephone" type="tel" required placeholder="+33 6 12 34 56 78" className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-900 mb-2">Groupe souhaité *</label>
                <select name="groupe" required className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 cursor-pointer bg-white">
                  <option value="">Choisissez votre groupe</option>
                  <option value="Groupe Femmes">Groupe Femmes — 3 places restantes</option>
                  <option value="Groupe Mixte">Groupe Mixte — 5 places restantes</option>
                </select>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-900 mb-2">Message (optionnel)</label>
                <textarea
                  name="message"
                  rows={3}
                  maxLength={500}
                  placeholder="Questions, besoins particuliers, régime alimentaire... (500 caractères max)"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                  onChange={(e) => setCharCount(e.target.value.length)}
                ></textarea>
                <p className="text-xs text-gray-400 mt-1 text-right">{charCount}/500</p>
              </div>
              {formStatus === 'error' && (
                <p className="text-red-500 text-sm mb-4 text-center">Une erreur est survenue. Veuillez réessayer.</p>
              )}
              <button
                type="submit"
                disabled={formStatus === 'sending'}
                className="w-full py-4 bg-gray-900 hover:bg-gray-700 text-white font-bold rounded-full text-base transition-all shadow-lg whitespace-nowrap cursor-pointer disabled:opacity-60"
              >
                {formStatus === 'sending' ? 'Envoi en cours...' : 'Envoyer ma demande de réservation'}
              </button>
              <p className="text-xs text-gray-400 text-center mt-4">Vos données sont traitées de manière confidentielle.</p>
            </form>
          )}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-amber-950 text-white py-16 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-10 mb-12">
            <div className="md:col-span-2">
              <p className="text-2xl font-extrabold tracking-tight mb-3">TribuVoyage</p>
              <p className="text-amber-200/70 text-sm leading-relaxed max-w-xs">
                Des séjours de déconnexion authentiques pour retrouver l'essentiel. En petit groupe, au cœur de la nature.
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-amber-400 font-semibold mb-5">Navigation</p>
              <div className="space-y-3">
                {['programme', 'activites', 'tarifs', 'faq', 'reservation'].map((s) => (
                  <button key={s} onClick={() => scrollTo(s)} className="block text-sm text-amber-100/70 hover:text-white transition-colors cursor-pointer capitalize">
                    {s === 'activites' ? 'Activités' : s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-amber-400 font-semibold mb-5">Newsletter</p>
              <p className="text-sm text-amber-100/60 mb-4">Recevez nos prochains séjours en avant-première.</p>
              <form data-readdy-form onSubmit={handleNewsletter} className="flex gap-2">
                <input name="email" type="email" required placeholder="votre@email.com" className="flex-1 bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:border-amber-400" />
                <button type="submit" className="px-4 py-2 bg-amber-500 hover:bg-amber-400 rounded-xl text-sm font-bold transition-all whitespace-nowrap cursor-pointer">OK</button>
              </form>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-amber-200/40 text-xs">© 2025 TribuVoyage · Renaissance au Soleil · Maroc 2026</p>
            <p className="text-amber-200/40 text-xs">11 – 17 mai 2026 · 1 899 € · 8 à 10 personnes</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
